import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@core-ui/Components/ui/button';
import { cn } from '@core-ui-lib/utils';

import { Check, AlertTriangle, XCircle, Trash2 } from 'lucide-react';

import { usePaginationData } from '@hooks/usePaginationData';

import type { ValidatedContact, CsvValidationStats } from '../../utils/validateCsvContacts';

type PreviewVariant = 'full' | 'compact';

interface CsvPreviewTableProps {
  contacts: ValidatedContact[];
  stats: CsvValidationStats;
  onRemoveContact?: (rowIndex: number) => void;
  onUpload: () => void;
  onCancel: () => void;
  isUploading: boolean;
  variant?: PreviewVariant;
  uploadableCount: number;
  canSubmit?: boolean;
}

interface ColumnDef {
  key: string;
  label: string;
}

function getColumns (): ColumnDef[] {
  return [
    { key: 'name', label: 'name' },
    { key: 'phone', label: 'phone' },
    { key: 'email', label: 'email' },
    { key: 'accountName', label: 'company' },
  ];
}

function StatusIcon ({ severity }: { severity: string }): React.JSX.Element {
  switch (severity) {
  case 'critical':
    return <XCircle className="h-5 w-5 text-red-500" />;
  case 'warning':
    return <AlertTriangle className="h-5 w-5 text-amber-500" />;
  default:
    return <Check className="h-5 w-5 text-emerald-500" />;
  }
}

function getFieldError (
  contact: ValidatedContact,
  fieldKey: string,
): { key: string; params?: Record<string, unknown>; color: string } | null {
  const error = contact.errors.find((e) => e.field === fieldKey);
  if (error == null) {
    return null;
  }

  const isCritical = error.type === 'missing';
  const color = isCritical ? 'text-red-500' : 'text-amber-500';

  // Manage missing errors
  if (error.type === 'missing') {
    if (fieldKey === 'phone') {
      return { key: 'contacts.errors.missingPhone', color };
    }
    if (fieldKey === 'name') {
      return { key: 'contacts.errors.missingName', color };
    }
    return { key: 'contacts.errors.missing', color };
  }

  // Manage format errors
  if (error.type === 'invalid_format') {
    if (fieldKey === 'phone') {
      return { key: 'contacts.errors.invalidPhoneFormat', color };
    }
    if (fieldKey === 'email') {
      return { key: 'contacts.errors.invalidEmail', color };
    }
    return { key: 'contacts.errors.invalid', color };
  }

  // Manage duplicated errors
  if (error.type === 'duplicate') {
    if (fieldKey === 'name') {
      return { key: 'contacts.errors.nameRepeat', params: { count: error.count }, color };
    }
    if (fieldKey === 'phone') {
      return { key: 'contacts.errors.phoneRepeat', params: { count: error.count }, color };
    }
    return { key: 'contacts.errors.duplicateContact', color };
  }

  return { key: error.type, color };
}

export default function CsvPreviewTable ({
  contacts,
  stats,
  onRemoveContact,
  onUpload,
  onCancel,
  isUploading,
  variant = 'full',
  uploadableCount,
  canSubmit
}: CsvPreviewTableProps): React.JSX.Element {
  const { t } = useTranslation();
  const columns = getColumns();
  const rowsPerPageDefault = variant === 'compact' ? 5 : 10;

  const {
    currentPage,
    setCurrentPage,
    currentData: paginatedContacts,
    totalItems,
  } = usePaginationData<ValidatedContact>({
    mode: 'client',
    data: contacts,
    initialRowsPerPage: rowsPerPageDefault,
  });

  const totalPages = Math.ceil(totalItems / rowsPerPageDefault);

  return (
    <div className="space-y-3">
      <div className={cn(
        'overflow-x-auto rounded-lg border border-border overflow-y-auto',
        variant === 'compact' ? 'max-h-[250px]' : 'max-h-100',
      )}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="w-10 px-3 py-2" />
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-3 py-2 text-left text-xs font-medium text-muted-foreground"
                >
                  {t(`contacts.table.${col.label}`)}
                </th>
              ))}
              {variant === 'full' && onRemoveContact !== undefined && (
                <th className="w-10 px-3 py-2" />
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedContacts.map((contact) => (
              <tr
                key={contact.rowIndex}
                className={cn(
                  'border-b border-border/60 last:border-b-0',
                  contact.severity === 'critical' && 'bg-red-50/50 dark:bg-red-950/20',
                  contact.severity === 'warning' && 'bg-amber-50/30 dark:bg-amber-950/10',
                )}
              >
                <td className="px-3 py-2.5">
                  <StatusIcon severity={contact.severity} />
                </td>
                {columns.map((col) => {
                  const value = (contact.data[col.key] ?? '').trim();
                  const fieldError = getFieldError(contact, col.key);
                  const isMissing = fieldError?.key.includes('missing');
                  return (
                    <td key={col.key} className="px-3 py-2.5">
                      <div>
                        <span className={cn((isMissing === true) && 'text-muted-foreground/50')}>
                          {(isMissing === true) ? '—' : (value !== '' ? value : '—')}
                        </span>
                        {fieldError !== null && (
                          <div className={cn('text-xs mt-0.5', fieldError.color)}>
                            {t(fieldError.key, fieldError.params)}                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
                {variant === 'full' && onRemoveContact !== undefined && (
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => {
                        onRemoveContact(contact.rowIndex); 
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-600 hover:text-red-800" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {(currentPage - 1) * rowsPerPageDefault + 1}-
            {Math.min(currentPage * rowsPerPageDefault, totalItems)}{' '}
            {t('contacts.csvPreview.of', 'de')} {totalItems}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => {
                setCurrentPage(currentPage - 1); 
              }}
              className="px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
            >
              &lsaquo;
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => {
                setCurrentPage(currentPage + 1); 
              }}
              className="px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
            >
              &rsaquo;
            </button>
          </div>
        </div>
      )}

      {(stats.critical > 0 || stats.warnings > 0) && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            {stats.critical > 0 && (
              <>
                {stats.critical} {t('contacts.csvPreview.criticalExcluded', 'contactos criticos seran excluidos.')}
              </>
            )}
            {stats.critical > 0 && stats.warnings > 0 && ' '}
            {stats.warnings > 0 && (
              <>
                {stats.warnings} {t('contacts.csvPreview.warningsIncluded', 'con advertencias se subiran marcados para revision.')}
              </>
            )}
          </span>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-1">
        <Button
          variant="outline"
          onClick={onCancel}
          className="border border-border text-muted-foreground hover:bg-muted"
        >
          {t('contacts.csvPreview.cancel', 'Cancelar')}
        </Button>
        <Button
          data-qa-id="create-list-form-button"
          onClick={onUpload}
          disabled={isUploading || uploadableCount === 0 || (canSubmit === false)}
          className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {t('contacts.csvPreview.upload', 'Subir')} {uploadableCount} {t('contacts.csvPreview.contacts', 'contactos')}
        </Button>
      </div>
    </div>
  );
}